import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { TESTIMONIALS } from "@/data/testimonials";
import { useAuth } from "@/lib/auth";
import ResourcesFooter from "@/components/ResourcesFooter";
import PortalMockup from "@/components/PortalMockup";
import LiveEntryScreencast from "@/components/LiveEntryScreencast";
import { orgSchema, productOffersSchema, faqSchema, jsonLdScript } from "@/lib/seo-schema";
import {
  Camera,
  Trophy,
  ListChecks,
  BarChart3,
  Download,
  Share2,
  Mail,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  FileDown,
  Merge,
  Users,
} from "lucide-react";

/**
 * Public marketing landing page at `/` for unauthenticated visitors.
 *
 * Logged-in visitors are routed through `RoleRouter` before they get here,
 * so this component never has to worry about an authenticated state — it
 * just sells the app.
 *
 * Visual language matches the existing auth screens: bone-black background,
 * amber-400 accents, generous whitespace, asymmetric hero. We deliberately
 * skip stock photography (per project guidelines) and lean on Lucide icons
 * + colour blocks so the page stays fast and on-brand.
 */
export default function Landing() {
  const navigate = useNavigate();

  // Fetch the summer-2026 promo flag on mount so the banner + Starter
  // ribbon disappear automatically the moment the campaign expires
  // (server-side date check — no client-side date drift). Failure is
  // silent: the section renders without the promo.
  const [promo, setPromo] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/public/promo/summer-2026`)
      .then((r) => { if (!cancelled) setPromo(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Only render testimonials that actually have a non-empty quote —
  // avoids leaving placeholder cards on the page after entries are
  // toggled off in /app/frontend/src/data/testimonials.js.
  const testimonials = (TESTIMONIALS || []).filter((t) => (t.quote || "").trim());

  // Answer-shaped FAQ used both for the visible teaser AND the FAQPage
  // JSON-LD. Duplication is intentional — AI answer engines cite the JSON
  // verbatim, so it needs to read as a self-contained answer, not JSX.
  const homeFaq = [
    {
      q: "What does Camera Club Score do?",
      a: "Camera Club Score is a web app for UK camera clubs to run internal photo competitions end-to-end: photographer roster, seasons, live scoring on judging night, placements, season league tables, printable scoresheets and results PDFs, and a public share portal so members can view results without an account.",
    },
    {
      q: "How much does Camera Club Score cost?",
      a: "Two plans, both £/year: Starter is £60/year for up to 20 photographers; Unlimited is £80/year with unlimited photographers. Both include a 30-day free trial and payment is via Stripe.",
    },
    {
      q: "Can I import results from my PDI competition software?",
      a: "Yes. The Live Entry screen accepts CSV, RTF and results PDFs in the standard 'Entrant Scores' or 'Images in Ranked Order' layouts most PDI competition tools produce. Placements, half-scores and photographer matching are handled automatically.",
    },
    {
      q: "Does Camera Club Score support PDI and print competitions?",
      a: "Yes — every competition is tagged with a type (PDI / Print) and a category (Open / Colour / Mono / Nature / Themed). A single season can mix any combination and they all appear side-by-side in the season league table.",
    },
    {
      q: "How is Photographer of the Year calculated?",
      a: "The trophy goes to whoever has the highest season total. How the total is built is configurable per club — every entry counts, best image per competition, or top two per competition. Individual rounds can be excluded from the season total if they're friendly rounds.",
    },
    {
      q: "Is our club's data private from other clubs on the platform?",
      a: "Yes — Camera Club Score is fully multi-tenant. Every club has an isolated workspace and the database enforces isolation on every query. Two clubs using the service never see each other's photographers, competitions, or results.",
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: "#0a0a0a", color: "#e5e5e5" }} data-testid="landing-page">
      <Helmet>
        <link rel="canonical" href="https://cameraclubscore.com/" />
        <meta property="og:url" content="https://cameraclubscore.com/" />
        <meta property="og:title" content="Camera Club Score — Photo Competition Software for Clubs" />
        <meta property="og:description" content="Run your club's photo competitions: live scoring, PDI & print, season league tables, PDI software import, printable PDFs. £60/year with a 30-day free trial." />
        <meta property="og:image" content="https://cameraclubscore.com/og-image.png" />
        <meta name="twitter:title" content="Camera Club Score — Photo Competition Software for Clubs" />
        <meta name="twitter:description" content="Run your club's photo competitions: live scoring, PDI & print, season league tables, PDI software import, printable PDFs. £60/year with a 30-day free trial." />
        <meta name="twitter:image" content="https://cameraclubscore.com/og-image.png" />
        {/* AEO — three separate JSON-LD blobs so answer engines can pick
            the graph they need: Organization for entity attribution,
            Product for pricing citations, FAQPage for zero-click answers. */}
        <script type="application/ld+json">{jsonLdScript(orgSchema())}</script>
        <script type="application/ld+json">{jsonLdScript(productOffersSchema())}</script>
        <script type="application/ld+json">{jsonLdScript(faqSchema(homeFaq))}</script>
        {testimonials.length > 0 && (
          <script type="application/ld+json">
            {jsonLdScript({
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              name: "Camera Club Score",
              applicationCategory: "BusinessApplication",
              operatingSystem: "Web",
              url: "https://cameraclubscore.com/",
              review: testimonials.map((t) => ({
                "@type": "Review",
                reviewBody: t.quote,
                author: {
                  "@type": "Person",
                  name: t.author || "Anonymous",
                  ...(t.club ? { affiliation: { "@type": "Organization", name: t.club } } : {}),
                },
              })),
            })}
          </script>
        )}
      </Helmet>
      <TopNav onSignIn={() => navigate("/login")} onSignUp={() => navigate("/register")} />
      <Hero onPrimaryCTA={() => navigate("/register")} />
      <FeatureGrid />
      <HowItWorks />
      <BuiltFor />
      <Pricing onCTA={() => navigate("/register")} promo={promo} />
      <Testimonials testimonials={testimonials} />
      <FaqTeaser homeFaq={homeFaq} />
      <FooterCTA onCTA={() => navigate("/register")} />
      <BottomFooter />
    </div>
  );
}


function TopNav({ onSignIn, onSignUp }) {
  return (
    <nav
      className="flex items-center justify-between px-6 lg:px-12 py-5"
      style={{ borderBottom: "1px solid #161616" }}
      data-testid="landing-nav"
    >
      <div className="flex items-center gap-2">
        <Camera className="text-amber-400" size={22} />
        <span className="font-semibold tracking-tight">Camera Club Score</span>
      </div>
      <div className="hidden md:flex items-center gap-6 text-sm muted-text">
        <a href="#features" className="hover:text-amber-400" data-testid="landing-nav-features">Features</a>
        <a href="#how" className="hover:text-amber-400" data-testid="landing-nav-how">How it works</a>
        <a href="#pricing" className="hover:text-amber-400" data-testid="landing-nav-pricing">Pricing</a>
        <Link to="/blog" className="hover:text-amber-400" data-testid="landing-nav-blog">Blog</Link>
        <Link to="/faq" className="hover:text-amber-400" data-testid="landing-nav-faq">FAQ</Link>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={onSignIn} className="ccs-btn-ghost text-sm" data-testid="landing-nav-signin">
          Sign in
        </button>
        <button onClick={onSignUp} className="ccs-btn text-sm" data-testid="landing-nav-signup">
          Get started
        </button>
      </div>
    </nav>
  );
}


function Hero({ onPrimaryCTA }) {
  return (
    <section className="px-6 lg:px-12 pt-20 pb-24 lg:pt-32 lg:pb-32 max-w-7xl" data-testid="landing-hero">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] gap-10 lg:gap-16 items-center">
        <div className="min-w-0">
          <div className="ccs-label mb-6 inline-flex items-center gap-2">
            <Sparkles size={12} className="text-amber-400" /> For UK camera clubs
          </div>

          <h1 className="text-5xl lg:text-7xl font-semibold leading-[1.05] tracking-tight mb-8">
            Run your club&rsquo;s competitions<br />
            <span className="text-amber-400">like a&nbsp;Saturday night TV final.</span>
          </h1>

          <p className="text-lg lg:text-xl muted-text max-w-2xl leading-relaxed mb-10">
            Camera Club Score is photo competition management software designed
            for UK camera clubs and photographic societies. Set up your roster,
            score live as the judge talks, generate the printable scoresheet,
            and publish a beautiful season league table — PDI or print, open
            or themed, every format covered.
          </p>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={onPrimaryCTA}
              className="ccs-btn flex items-center gap-2 text-base px-5 py-3"
              data-testid="landing-hero-primary-cta"
            >
              Start 30-day free trial <ArrowRight size={16} />
            </button>
          </div>

          <p className="muted-text text-sm mt-5 max-w-xl" data-testid="landing-hero-portal-blurb">
            Every club also gets a <span className="text-amber-400">public portal</span> — a
            no-login page for your members and the public showing the season&rsquo;s
            competitions and results. Reset the link any time to revoke access.
          </p>

          <div className="muted-text text-xs mt-6 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-400" /> No credit card</span>
            <span className="inline-flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-400" /> One-click data export</span>
            <span className="inline-flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-400" /> Works on the night, on any tablet</span>
          </div>
        </div>

        <div className="lg:pl-4">
          <PortalMockup />
        </div>
      </div>
    </section>
  );
}


const FEATURES = [
  {
    icon: Camera,
    title: "Photographer roster",
    body: "Upload via CSV or add one at a time. Auto-generated codes, photo-credit aliases, and an active/inactive toggle for retired members.",
  },
  {
    icon: ListChecks,
    title: "Live scoring grid",
    body: "Built for judging night. One row per image, score and placement in the same keystroke, autosave, no spreadsheet shuffling.",
  },
  {
    icon: Sparkles,
    title: "PDI software import",
    body: "Drop in the RTF, CSV or PDF your PDI software already spits out. Placements, half-scores and photographer matching are handled automatically — no re-typing into a spreadsheet.",
  },
  {
    icon: Trophy,
    title: "Seasons & weighting",
    body: "Run a full season of rounds, mark counted vs friendly competitions, and let the season league table crown your Photographer of the Year — by total score, best average, or your own pick of best-image-per-round.",
  },
  {
    icon: BarChart3,
    title: "Printable reports & PDFs",
    body: "Scoresheets, judges&rsquo; results, and end-of-season tables — generated in one click, designed for laminating and pinning on the clubhouse wall.",
  },
  {
    icon: FileDown,
    title: "Awards-night mail-merge",
    body: "Two one-click CSVs for presentation night: every top-scoring image, and every 1st place. Same column layout, drop into your certificate template, done.",
  },
  {
    icon: Share2,
    title: "Public portal",
    body: "Generate a read-only share link so members can browse competition results on their phones without an account. Disable any time.",
  },
  {
    icon: Users,
    title: "Roster cleanup tools",
    body: "Import a messy spreadsheet, then merge duplicates (typos, nicknames and initials all caught) or bulk-delete retired members in one pass. Full-name field means &ldquo;John George Doo&rdquo; just works.",
  },
  {
    icon: Download,
    title: "One-click backups",
    body: "Download your entire club as a single JSON file. Restore from any backup in seconds — merge missing rows, or wipe and rebuild.",
  },
];


function FeatureGrid() {
  return (
    <section
      id="features"
      className="px-6 lg:px-12 py-20"
      style={{ background: "#0f0f0f", borderTop: "1px solid #161616", borderBottom: "1px solid #161616" }}
      data-testid="landing-features"
    >
      <div className="max-w-6xl">
        <div className="ccs-label mb-3">Features</div>
        <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight mb-3">
          The whole competition workflow.
        </h2>
        <p className="muted-text max-w-2xl mb-12">
          From the moment a season starts to the awards-night printout, everything
          you used to do in three Excel files lives in one place.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: "#1f1f1f" }}>
          {FEATURES.map((f, i) => (
            <div
              key={i}
              className="p-8 transition-colors hover:bg-neutral-900"
              style={{ background: "#0a0a0a" }}
              data-testid={`landing-feature-${i}`}
            >
              <f.icon className="text-amber-400 mb-5" size={24} strokeWidth={1.6} />
              <h3 className="text-base font-semibold mb-2">{f.title}</h3>
              <p className="muted-text text-sm leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


const STEPS = [
  {
    n: "01",
    title: "Create your club",
    body: "Sign up with your email, pick a club name, and you&rsquo;re in. Your data stays isolated — no other club can see it.",
  },
  {
    n: "02",
    title: "Invite your team",
    body: "Drop in your Secretary and any committee members by email. Each gets a one-time link to set their own password.",
  },
  {
    n: "03",
    title: "Score the night",
    body: "On competition night, open the live entry grid, type scores as the judge announces them. Done in real time, printable instantly.",
  },
  {
    n: "04",
    title: "Publish results",
    body: "PDF the scoresheet, share the public portal link with your members, and the season league table updates itself.",
  },
];


function HowItWorks() {
  return (
    <section id="how" className="px-6 lg:px-12 py-20 max-w-7xl" data-testid="landing-how">
      <div className="ccs-label mb-3">How it works</div>
      <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight mb-12">
        Four steps from sign-up to season trophies.
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-10 lg:gap-16 items-start">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {STEPS.map((s, i) => (
            <div key={i} className="relative" data-testid={`landing-step-${i}`}>
              <div className="mono text-xs muted-text mb-3 tracking-widest">{s.n}</div>
              <h3 className="text-lg font-semibold mb-2">{s.title}</h3>
              <p
                className="muted-text text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: s.body }}
              />
            </div>
          ))}
        </div>
        <div className="lg:sticky lg:top-8">
          <LiveEntryScreencast />
        </div>
      </div>
    </section>
  );
}


/**
 * "Built for" section — copy is intentionally rich in long-tail
 * keywords (PDI competition software, print competition software,
 * camera club judging, etc.) without being keyword-stuffed. Each
 * sentence reads naturally to a human; the search engines just happen
 * to find what they're looking for in the same prose.
 */
function BuiltFor() {
  return (
    <section
      className="px-6 lg:px-12 py-20"
      style={{ background: "#0f0f0f", borderTop: "1px solid #161616", borderBottom: "1px solid #161616" }}
      data-testid="landing-built-for"
    >
      <div className="max-w-6xl">
        <div className="ccs-label mb-3">Who&rsquo;s it for</div>
        <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight mb-3">
          Built for the people who run camera clubs.
        </h2>
        <p className="muted-text max-w-2xl mb-12 leading-relaxed">
          Whether you&rsquo;re a small club running monthly PDI competitions,
          a large photographic society with a packed print &amp; projected
          season, or a regional federation organising inter-club battles,
          Camera Club Score has the workflow.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <UseCase
            title="PDI competition software"
            body="Score projected digital images live as the judge announces. Auto-sequenced entry order, in-line score &amp; placement, instant scoresheet PDF."
            n="0"
          />
          <UseCase
            title="Print competition software"
            body="Run print rounds with the same scoring grid. Track image titles, photographer codes, and placement awards. Generate clean printable results for your noticeboard."
            n="1"
          />
          <UseCase
            title="Season league tables"
            body="Photographic society scoring done right. Season totals and averages, &ldquo;counted&rdquo; vs friendly competition flags, configurable rules for whether every image counts or only the best per round."
            n="2"
          />
          <UseCase
            title="Club competition scoring &amp; results"
            body="Camera club results software that does the post-match admin for you: PDFs, season standings, and a public results portal — all generated automatically."
            n="3"
          />
          <UseCase
            title="Camera club administration"
            body="Photographer roster, member accounts with roles (Admin / Secretary / Viewer), email invitations, one-click backups. The whole back-office, included."
            n="4"
          />
        </div>
      </div>
    </section>
  );
}


function UseCase({ title, body, n }) {
  return (
    <div
      className="p-6"
      style={{ background: "#0a0a0a", border: "1px solid #1f1f1f" }}
      data-testid={`landing-usecase-${n}`}
    >
      <h3 className="text-base font-semibold mb-2 text-amber-400">{title}</h3>
      <p className="muted-text text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: body }} />
    </div>
  );
}


function Testimonials({ testimonials }) {
  if (!testimonials?.length) return null;
  return (
    <section
      className="px-6 lg:px-12 py-20 max-w-5xl"
      data-testid="landing-testimonials"
    >
      <div className="ccs-label mb-3">What clubs say</div>
      <div className="grid grid-cols-1 gap-8">
        {testimonials.map((t, i) => (
          <figure
            key={i}
            className="relative pl-8 md:pl-12"
            data-testid={`landing-testimonial-${i}`}
          >
            {/* Big serif opening quote — pure decoration so screen readers
                skip it (aria-hidden). Kept in-flow so text reflows on
                narrow screens without an absolute pin. */}
            <span
              aria-hidden="true"
              className="absolute left-0 top-0 select-none"
              style={{
                fontFamily: "'Georgia', 'Times New Roman', serif",
                fontSize: "5rem",
                lineHeight: 1,
                color: "#fbbf24",
                opacity: 0.35,
              }}
            >
              &ldquo;
            </span>
            <blockquote
              className="text-xl md:text-2xl leading-relaxed font-medium"
              style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
            >
              {t.quote}
            </blockquote>
            <figcaption className="mt-4 mono text-xs uppercase tracking-[0.25em] muted-text">
              — {t.author || "Anonymous"}
              {t.role ? `, ${t.role}` : ""}
              {t.club ? (
                <span className="text-amber-400">
                  {"  ·  "}
                  {t.club}
                </span>
              ) : null}
              {t.location ? <span> · {t.location}</span> : null}
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}


function Pricing({ onCTA, promo }) {
  const promoActive = !!promo?.active;
  return (
    <section
      id="pricing"
      className="px-6 lg:px-12 py-20"
      style={{ background: "#0f0f0f", borderTop: "1px solid #161616", borderBottom: "1px solid #161616" }}
      data-testid="landing-pricing"
    >
      <div className="max-w-5xl">
        <div className="ccs-label mb-3">Pricing</div>
        <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight mb-3">
          Simple, per-club pricing.
        </h2>
        <p className="muted-text text-base leading-relaxed mb-6 max-w-2xl">
          One flat annual fee per club. No per-user seats, no per-competition
          charges, no card required to start &mdash; every new club gets a
          full 30-day trial before any billing kicks in.
        </p>

        {promoActive && (
          <div
            className="mb-8 p-4 flex items-start gap-3"
            style={{ background: "#1a1000", border: "1px solid #fbbf24", color: "#fbbf24" }}
            data-testid="landing-pricing-summer-banner"
          >
            <Sparkles size={18} className="mt-0.5 shrink-0" />
            <div className="text-sm leading-relaxed">
              <strong className="tracking-wide uppercase text-xs">Launch offer</strong>
              <br />
              For new clubs signing up before <strong>{promo.ends_label}</strong>,
              the Starter plan comes with <strong>unlimited photographers</strong> —
              no 20-photographer cap. Grab the £60/year price with the £80 feature set
              while the offer lasts.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Starter */}
          <div
            className="p-8 relative"
            style={{
              background: "#0a0a0a",
              border: promoActive ? "1px solid #fbbf24" : "1px solid #2a2a2a",
            }}
            data-testid="landing-pricing-starter"
          >
            {promoActive && (
              <div
                className="absolute -top-3 left-6 px-2 py-1 text-[10px] tracking-widest uppercase font-medium"
                style={{ background: "#fbbf24", color: "#0a0a0a" }}
                data-testid="landing-pricing-starter-ribbon"
              >
                Launch offer — unlimited
              </div>
            )}
            <div className="ccs-label mb-3">Starter</div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-5xl font-semibold tracking-tight">£60</span>
              <span className="muted-text text-sm">/ year</span>
            </div>
            <div className="muted-text text-xs mb-6">≈ £5/month, billed annually</div>
            <ul className="text-sm muted-text space-y-2">
              {promoActive ? (
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={14} className="text-amber-400 mt-0.5" />
                  <span>
                    <strong className="text-amber-400">Unlimited photographers</strong>{" "}
                    <span className="mono text-[10px] muted-text">
                      (launch offer, was 20)
                    </span>
                  </span>
                </li>
              ) : (
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5" /> Up to <strong className="text-white">20 photographers</strong></li>
              )}
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5" /> Unlimited seasons &amp; competitions</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5" /> Full PDF / CSV export</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5" /> Public portal sharing</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5" /> One-click data backup</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5" /> Email support</li>
            </ul>
            <button
              onClick={onCTA}
              className="ccs-btn-ghost w-full mt-7 justify-center"
              data-testid="landing-pricing-cta-starter"
            >
              Start 30-day free trial <ArrowRight size={14} />
            </button>
          </div>

          {/* Unlimited */}
          <div
            className="p-8 relative"
            style={{ background: "#0a0a0a", border: "1px solid #fbbf24" }}
            data-testid="landing-pricing-unlimited"
          >
            <div
              className="absolute -top-3 left-6 px-2 py-1 text-[10px] tracking-widest uppercase font-medium"
              style={{ background: "#fbbf24", color: "#0a0a0a" }}
            >
              Most flexible
            </div>
            <div className="ccs-label mb-3">Unlimited</div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-5xl font-semibold tracking-tight">£80</span>
              <span className="muted-text text-sm">/ year</span>
            </div>
            <div className="muted-text text-xs mb-6">≈ £6.67/month, billed annually</div>
            <ul className="text-sm muted-text space-y-2">
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-amber-400 mt-0.5" /> <strong className="text-white">Unlimited photographers</strong></li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-amber-400 mt-0.5" /> Unlimited seasons &amp; competitions</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-amber-400 mt-0.5" /> Full PDF / CSV export</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-amber-400 mt-0.5" /> Public portal sharing</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-amber-400 mt-0.5" /> One-click data backup</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-amber-400 mt-0.5" /> Priority email support</li>
            </ul>
            <button
              onClick={onCTA}
              className="ccs-btn w-full mt-7 justify-center"
              data-testid="landing-pricing-cta-unlimited"
            >
              Start 30-day free trial <ArrowRight size={14} />
            </button>
          </div>
        </div>

        <p className="muted-text text-xs mt-6 max-w-2xl">
          Existing clubs on the beta plan are grandfathered indefinitely
          &mdash; no charge unless you choose to upgrade. Cancel any time
          from your billing dashboard; access continues to the end of the
          paid period.
        </p>
      </div>
    </section>
  );
}


function FaqTeaser({ homeFaq }) {
  return (
    <section id="faq" className="px-6 lg:px-12 py-20 max-w-6xl" data-testid="landing-faq-teaser">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
        <div>
          <div className="ccs-label mb-3">Questions?</div>
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight mb-4">
            We&rsquo;ve probably already answered it.
          </h2>
          <p className="muted-text leading-relaxed mb-6">
            The full FAQ covers everything from scoring rules to inviting
            members to recovering a deleted competition. Still stuck? Drop us
            a line.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/faq" className="ccs-btn flex items-center gap-2" data-testid="landing-faq-cta">
              Read the FAQ <ArrowRight size={14} />
            </Link>
            <a
              href="mailto:mail@cameraclubscore.com"
              className="ccs-btn-ghost flex items-center gap-2"
              data-testid="landing-faq-contact"
            >
              <Mail size={14} /> Email us
            </a>
          </div>
        </div>

        {/* Question + answer are shown together so the visible copy
            matches the FAQPage JSON-LD verbatim — Google penalises
            schema whose answers don't appear on the page. */}
        <div className="space-y-3">
          {homeFaq.map((item, i) => (
            <details
              key={i}
              className="p-4 group"
              style={{ background: "#0f0f0f", border: "1px solid #1f1f1f" }}
              data-testid={`landing-faq-q-${i}`}
              {...(i === 0 ? { open: true } : {})}
            >
              <summary className="flex items-start gap-3 cursor-pointer list-none">
                <span className="muted-text mono text-xs mt-0.5 shrink-0">Q.</span>
                <span className="font-medium flex-1">{item.q}</span>
                <ArrowRight
                  size={14}
                  className="mt-0.5 shrink-0 text-amber-400 transition-transform group-open:rotate-90"
                />
              </summary>
              <div className="mt-3 pl-6 text-sm muted-text leading-relaxed">
                {item.a}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}


function FooterCTA({ onCTA }) {
  return (
    <section
      className="px-6 lg:px-12 py-24 text-center"
      style={{ background: "#0f0f0f", borderTop: "1px solid #161616" }}
      data-testid="landing-final-cta"
    >
      <h2 className="text-3xl lg:text-5xl font-semibold tracking-tight mb-4">
        Your next competition night starts here.
      </h2>
      <p className="muted-text text-base mb-8 max-w-xl mx-auto">
        Sign up in under a minute. Bring your own photographers; we&rsquo;ll
        bring the scoring grid, the PDFs, and the league table.
      </p>
      <button
        onClick={onCTA}
        className="ccs-btn inline-flex items-center gap-2 px-6 py-3 text-base"
        data-testid="landing-final-cta-button"
      >
        Create your club <ArrowRight size={14} />
      </button>
    </section>
  );
}


function BottomFooter() {
  return (
    <footer
      className="px-6 lg:px-12 py-12"
      style={{ background: "#0a0a0a", borderTop: "1px solid #161616" }}
      data-testid="landing-footer"
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 max-w-6xl">
        <div className="flex items-center gap-2">
          <Camera className="text-amber-400" size={18} />
          <span className="font-semibold text-sm">Camera Club Score</span>
        </div>
        <ResourcesFooter />
        <div className="muted-text text-xs">
          &copy; {new Date().getFullYear()} Camera Club Score. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
